import React, { useEffect, useRef, useState } from 'react';

import { IntlProvider } from 'react-intl';
import { ErrorBoundary } from 'react-error-boundary';

import ConversationAssistantWidget from '@atlassian/conversation-assistant-widget/async';
import type { ConversationAssistantActions } from '@atlassian/conversation-assistant-widget/types';
import FabricAnalyticsListeners from '@atlaskit/analytics-listeners/FabricAnalyticsListeners';
import { tenantType } from '@atlassiansox/analytics-web-client/analytics-web-types';
import { AIMateAnalyticsContext } from '@atlaskit/analytics-namespaced-context/AIMateAnalyticsContext';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { AnonymousAuthProvider } from '@atlassian/conversation-assistant-widget/csm-widget-access-provider-service';
import { RendererCardsAsLinksExtension as rendererCardsAsLinksExtension } from '@atlassian/conversation-assistant-widget/renderer-cards-as-links-extension-components';
import { TwilioExtensionComponents as twilioExtensionComponents } from '@atlassian/conversation-assistant-widget/twilio-extension-components';

import { createPostMessageBridge } from '../../../post-message-api';
import { defaultLocale } from '../../../constants';
import { createChatStatusPromise } from '../utils';
import type { CSMWidgetEgressMessage, CSMWidgetIngressMessage } from '../../../sdk/widget/types';
import { perfNow } from '../../../utils/performance';
import { isBubbleImageLoadPending } from '../../../utils/bubble-image-load-times';
import { client } from '../analytics-client';
import {
	initUFOV1,
	markBubbleImageLoadAndFMP,
	markWidgetLoadStart,
	panelLoadExperience,
	widgetLoadExperience,
	widgetOpenToOnlineExperience,
	WidgetLoadExperienceMarks,
} from '../ufo';
import type { InitMessage } from '../../../sdk/types';
import { OauthAuthProvider } from '../auth';
import { useAssistantConfig } from '../useAssistantConfig';

type CSMWidgetIframeMessages = {
	subscribable: CSMWidgetIngressMessage;
	sendable: CSMWidgetEgressMessage;
};

const { send, on } = createPostMessageBridge<CSMWidgetIframeMessages>({
	target: window.parent,
	origin: '*',
});

initUFOV1();

const IFRAME_LOAD_END_TIME = perfNow();
panelLoadExperience.start(0); // start as early as possible

const { waitForChatToBeOnline, notifyChatIsOnline } = createChatStatusPromise();

const EXTENSION_COMPONENTS = {
	...twilioExtensionComponents,
	...rendererCardsAsLinksExtension,
};

export const IframeableWidgetPanel = (): React.JSX.Element | null => {
	const actionsRef = useRef<ConversationAssistantActions>(null);
	const hasMarkedWidgetOpened = useRef(false);
	const isChatOnlineRef = useRef(false);
	const hasMarkedFMP = useRef(false);
	const instanceInitTimeRef = useRef<number | undefined>(undefined);
	const pendingMetricsRef = useRef<InitMessage['payload']['metrics']>(undefined);
	const [isVisible, setIsVisible] = useState(false);
	const [config, setConfig] = useState<InitMessage['payload']>();
	const [oAuthProvider, setOAuthProvider] = useState<OauthAuthProvider>();
	const [anonymousAuthProvider, setAnonymousAuthProvider] = useState<AnonymousAuthProvider>();

	useEffect(() => {
		on('init', (payload) => {
			if (payload.tenantId) {
				client.setTenantInfo(tenantType.CLOUD_ID, payload.tenantId);
			}
			if (payload.metrics) {
				instanceInitTimeRef.current = payload.metrics.instanceInitTime;
				pendingMetricsRef.current = payload.metrics;
				markWidgetLoadStart({ ...payload.metrics, iframeLoadEndTime: IFRAME_LOAD_END_TIME });
				hasMarkedFMP.current = !isBubbleImageLoadPending(payload.metrics);
			} else {
				hasMarkedFMP.current = true;
			}
			if (payload.auth?.type === 'oauth') {
				setOAuthProvider(new OauthAuthProvider({ send, on }, payload.auth.accessToken));
			}
			if (payload.auth?.type === 'anonymous') {
				setAnonymousAuthProvider(
					new AnonymousAuthProvider({ anonAuthToken: payload.auth.anonAuthToken }),
				);
			}
			setConfig(payload);
		});

		on('INITIATE_CONVERSATION', ({ message }) => {
			waitForChatToBeOnline.then(() => {
				actionsRef.current?.createNewConversation({
					message,
				});
			});
		});

		on('get-conversation', ({ conversationId }) => {
			const conversation = actionsRef.current?.getConversation({
				conversationId,
			});

			send('get-conversation-result', {
				conversationId,
				result: conversation
					? {
							conversationId: conversation.conversationId,
							messages: conversation.messages,
						}
					: null,
			});
		});

		on('get-current-conversation', () => {
			const conversation = actionsRef.current?.getCurrentConversation();

			send(
				'get-current-conversation-result',
				conversation
					? {
							conversationId: conversation.conversationId,
							messages: conversation.messages,
						}
					: null,
			);
		});

		on('bubble-image-load', ({ startTime, endTime }) => {
			const baseMetrics = pendingMetricsRef.current;
			if (!baseMetrics || hasMarkedFMP.current) {
				return;
			}

			markBubbleImageLoadAndFMP({
				scriptLoadStartTime: baseMetrics.scriptLoadStartTime,
				iframeInitTime: baseMetrics.iframeInitTime,
				bubbleImageLoadStartTime: startTime,
				bubbleImageLoadEndTime: endTime,
			});
			hasMarkedFMP.current = true;
			if (isChatOnlineRef.current) {
				widgetLoadExperience.success();
			}
		});

		on('open-state-change', ({ isOpen, openedAt }) => {
			setIsVisible(isOpen);

			if (isOpen && !hasMarkedWidgetOpened.current) {
				hasMarkedWidgetOpened.current = true;

				if (isChatOnlineRef.current) {
					widgetOpenToOnlineExperience.start();
					widgetOpenToOnlineExperience.success();
				} else if (typeof instanceInitTimeRef.current === 'number') {
					widgetOpenToOnlineExperience.start(instanceInitTimeRef.current - openedAt);
				} else {
					widgetOpenToOnlineExperience.start();
				}
			}
		});
		send('ready');
	}, []);

	const assistantConfig = useAssistantConfig(config?.credentials, config?.conversationContext);

	if (!config) {
		return null;
	}

	return (
		<ErrorBoundary
			fallback={null}
			onError={() => {
				widgetLoadExperience.failure();
				panelLoadExperience.failure();
				widgetOpenToOnlineExperience.failure();
			}}
		>
			<FabricAnalyticsListeners
				// @ts-expect-error - Type 'GasPurePayload' is not assignable to type 'UITrackAndOperationalEventPayload'
				client={client}
			>
				<AIMateAnalyticsContext data={config.analyticsContext ?? {}}>
					<IntlProvider
						defaultLocale={defaultLocale}
						locale={fg('csm_widget_embed_i18n') ? (config.locale ?? defaultLocale) : defaultLocale}
					>
						<ConversationAssistantWidget
							sessionId={config.sessionId}
							restoreConversationId={config?.conversationId}
							oAuthProvider={oAuthProvider}
							anonymousAuthProvider={anonymousAuthProvider}
							onHasUnreadMessagesChanged={(hasUnreadMessages) => {
								send('HAS_UNREAD_MESSAGES_CHANGED', { hasUnreadMessages });
							}}
							uiConfig={{
								conciergeGreeting: true,
								elements: {
									errors: {
										default: config.uiConfig?.errors?.message,
									},
									chatInput: {
										placeholder: config.uiConfig?.chatInput?.placeholder,
									},
								},
							}}
							onCloseChat={() => {
								send('CLOSE_WIDGET');
							}}
							onChatStatus={(status) => {
								if (status === 'launching') {
									widgetLoadExperience.mark(WidgetLoadExperienceMarks.COMPONENT_LAUNCHING);
								}
								if (status === 'online') {
									isChatOnlineRef.current = true;
									notifyChatIsOnline();
									panelLoadExperience.success();
									widgetLoadExperience.mark(WidgetLoadExperienceMarks.COMPONENT_ONLINE);
									if (hasMarkedFMP.current) {
										widgetLoadExperience.success();
									}
									widgetOpenToOnlineExperience.success();
								}
								send('status', { status });
							}}
							onChatMessage={{
								before: (payload) => {
									send('message:before', {
										conversationId: payload.conversationId,
										message: payload.message,
									});
								},
								started: (payload) => {
									send('message:started', {
										conversationId: payload.conversationId,
										message: payload.message,
									});
								},
								aborted: (payload) => {
									send('message:aborted', {
										conversationId: payload.conversationId,
										message: payload.message,
									});
								},
								completed: (payload) => {
									send('message:completed', {
										conversationId: payload.conversationId,
										message: payload.message,
									});
								},
								failed: (payload) => {
									send('message:failed', {
										conversationId: payload.conversationId,
										message: payload.message,
									});
								},
							}}
							onError={(payload) => {
								widgetLoadExperience.failure();
								send('error', {
									message: payload.message,
								});
							}}
							actionsRef={actionsRef}
							config={assistantConfig}
							isVisible={isVisible}
							extensionComponents={
								fg('csm_ai_override_renderer_cards_as_links')
									? EXTENSION_COMPONENTS
									: twilioExtensionComponents
							}
						/>
					</IntlProvider>
				</AIMateAnalyticsContext>
			</FabricAnalyticsListeners>
		</ErrorBoundary>
	);
};
